/* eslint-disable reos/no-hardcoded-api-text -- popisky menu jsou demo UI; i18n vrstva web-adminu zatim neexistuje (vzor capabilities.ts) */

import type { ComponentType } from 'react';
/**
 * Krok BRAND2 bod 8 — sest polozek menu nese LINIOVE IKONY ZNACKY „Náves"
 * (`docs/design/brand-naves/ikony/ui/`, str. 12): Dnešek, Zakázky, Nová,
 * Kalendář, Klienti a Reporty. Jsou to presne ty, pro ktere znacka ikonu
 * dodala a ktere na ni sedi 1:1 — pet z nich je zaroven spodni lista mobilu.
 * Zbytek menu zustava na `lucide-react`, protoze jina ikona znacky
 * neexistuje a vymyslet ji by znamenalo kreslit do identity.
 */
import {
  BrandIconDnesek,
  BrandIconKalendar,
  BrandIconKlienti,
  BrandIconNova,
  BrandIconReport,
  BrandIconZakazky,
} from '../components/brand-icons.js';
import {
  Bell,
  Building2,
  Search,
  GitCompareArrows,
  CalendarClock,
  Handshake,
  CircleCheck,
  RefreshCw,
  Megaphone,
  Activity,
  Coins,
  MessageSquareReply,
  Gavel,
  Globe,
  LayoutTemplate,
  IdCard,
  Layers,
  UserRoundCheck,
  Mail,
  Send,
  MessageSquare,
  BellRing,
  AtSign,
  FileText,
  FileSignature,
  PenLine,
  ReceiptText,
  Wallet,
  Inbox,
  Percent,
  Landmark,
  TrendingUp,
  BarChart3,
  LineChart,
  Scale,
  Share2,
  ShieldCheck,
  Network,
  Database,
  Upload,
  Lock,
  HardDriveDownload,
  Server,
  KeyRound,
  Settings,
  ScrollText,
  ClipboardCheck,
  ClipboardList,
  Radar,
  Sparkles,
  ShoppingCart,
  /** Krok APP2 bod 3: ikona „Nápověda" (`HELP_ITEM`). */
  CircleQuestionMark,
} from 'lucide-react';

/**
 * Sidebar navigace (BLOK 2.5, docs/platform/ui/NAVIGACE-A-IKONY.md): 9
 * sekci pokryvajicich vsech 41 oblasti Realman parity + to, co Realman
 * nema (audit, schvaleni). Polozky bez obsahu vedou na stranku PRIPRAVUJE
 * SE (texty v lib/texts.ts), nikdy na 404.
 *
 * Role-gatovane polozky (audit, schvaleni, uzivatele a role, provize) maji
 * `action` — viditelnost resi capabilities.ts (isNavItemVisible), stejna
 * logika jako drive „Interni" sekce, ted uz bez duplicitniho menu.
 */

/**
 * Krok UX1 — skupina, do ktere patří preparing sekce (AUD1 §3.2/§3.3):
 *  - `soon`   = ukázat s jednotným štítkem „Obchod brzy“,
 *  - `hidden` = „skrýt do napojení“, výchozí stav NEVIDÍ; odemyká ji
 *               přepínač per tenant (`pilot.showHiddenSections`).
 * Bez této hodnoty je přiřazení jen domysl — proto ji musí mít každá
 * preparing položka (test v nav.spec.ts).
 */
export type PilotVisibility = 'soon' | 'hidden';

/**
 * Krok UXN1 (R.B. 9. 10. 2026 20:17: ANO) — LEVE MENU = VSECHNY PRACOVNI
 * MODULY, seskupene tematicky. Nahrazuje UXV4a/UXR3 „menu podle role +
 * nastroje v profilu" (nalez auditu GPT: makler hledal Úkoly, Smlouvy,
 * Nemovitosti… v dlouhem posuvnem avatar menu mezi jazykem a vzhledem).
 *
 * Peti skupin podle zadani:
 *  - `obchod`   — Zakázky, Nemovitosti, Poptávky, Akvizice, Portály a inzerce
 *  - `prace`    — Úkoly, Kalendář, Klienti, Smlouvy, Prohlídky
 *  - `penize`   — Fakturace, Náklady na inzerci, Ekonomika kanceláře
 *  - `prehledy` — Reporty (Statistiky zustava PRIPRAVUJE SE, viz nize)
 *  - `kancelar` — Kampaně, Asistentka, Schválení, Správa nájmů, Nastavení
 *
 * „Dnešek" a akce „Nová zakázka" stoji NAD skupinami, bez nadpisu a bez
 * `menuGroup` (pinned, vzdy rozbalene — sklada je primo `layout.tsx`).
 * Role-gating (`ownerAdminOnly`, `action`) zustava stejny jako driv —
 * resi ho `isNavItemVisible` v `capabilities.ts`, jen uz nerozhoduje o tom,
 * jestli polozka ma vstup v menu, nebo v profilu (profil uz zadny pracovni
 * modul nenese).
 *
 * ROZHODNUTI UXN1-1: „Statistiky" (preparing+hidden) do skupiny Přehledy
 * nejde, dokud nebude hotova — zadna preparing polozka v MENU_ITEMS (hlida
 * test nize), stejne jako driv.
 * ROZHODNUTI UXN1-2: polozky, ktere zadani nejmenuje vyslovne (Párování
 * poptávek, Smlouvy a šablony, Audit, Nepřiřazené náklady), stoji u svych
 * nejblizsich pribuznych (poptavky/smlouvy/kancelar/penize) — zadna route
 * nezmizela, jen zmenila skupinu.
 *
 * Polozka bez `menuGroup` (preparing nebo `hiddenFromMenu`) se ve skutecnem
 * menu (panel, Ctrl+K) nevykresluje vubec — `NAV_SECTIONS`/10 tematickych
 * sekci tim zustavaji beze zmeny jako zdroj pravdy pro PREPARING_PATHS
 * presmerovani (router.tsx) a pro testy BLOKu 2.5, jen uz nerozhoduji o tom,
 * co vidi maklet v menu.
 */
export type MenuGroup = 'obchod' | 'prace' | 'penize' | 'prehledy' | 'kancelar';

export interface Blok1NavItem {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  /** Sekce zatim bez obsahu — cil je stranka PRIPRAVUJE SE. */
  readonly preparing?: boolean;
  /** Krok UX1: viz `PilotVisibility`. Povinne u kazde `preparing` polozky. */
  readonly pilotVisibility?: PilotVisibility;
  /** Dalsi prefixy cest, pri kterych je polozka aktivni (detaily, kokpity). */
  readonly activePrefixes?: readonly string[];
  /** Ikona (lucide-react) — nikdy nenese vyznam sama, vzdy doprovazi label. */
  readonly icon: ComponentType<{ readonly size?: number; readonly strokeWidth?: number }>;
  /** Policy akce (@reos/policy) — kdyz je nastavena, polozku filtruje role. */
  readonly action?: string;
  /**
   * Krok UXA1: polozka smi do skutecneho menu, jen kdyz je prihlaseny
   * owner/admin — NAD RAMEC `action` (ten muze mit grant i jina role, napr.
   * `legal` u `audit.log.read"). Server strani pristup sve API resi sam
   * (napr. `admin-campaigns.ts` kontroluje roli primo); tohle je jen to, co
   * uvidi v menu bezny maklet (R.B. 1. 10.: „Jen pro roli správce/owner").
   */
  readonly ownerAdminOnly?: boolean;
  /**
   * Krok UXA1: zdroj pravdy (NAV_SECTIONS) polozku zna, ale v menu (sidebar,
   * Ctrl+K) se nevykresluje — ma vlastni vstupni bod (pocitadlo na Dnesku
   * a v detailu zakazky u `k-doplneni`, filtr „Podepisuje se" ve Smlouvach
   * u `elektronicke-podpisy`). Routa zustava funkcni, zadne 404.
   */
  readonly hiddenFromMenu?: boolean;
  /** Krok UXA1: do ktere ze tri skutecne vykreslenych skupin polozka patri. */
  readonly menuGroup?: MenuGroup;
  /**
   * Krok T5 bod 3: polozka nese pocitadlo (mockup ma u „Elektronických
   * podpisů" odznak s poctem cekajicich). Klic rika, CO se pocita; cislo
   * dotahuje `layout.tsx`, aby menu nezustalo zavisle na jednom endpointu.
   */
  readonly badge?: 'pendingSignatures';
}

export interface Blok1NavSection {
  readonly id: string;
  readonly label: string;
  readonly items: readonly Blok1NavItem[];
}

export const NAV_SECTIONS: readonly Blok1NavSection[] = [
  {
    id: 'prehled',
    label: 'Přehled',
    items: [
      // Krok UXV4a: ikona domu podle predlohy v4 (spodni lista `Main.dc.html`).
      // Krok UXN1: „Dnešek" stoji NAD skupinami (pinned, bez menuGroup) —
      // viz `layout.tsx`.
      { id: 'dnesek', label: 'Dnešek', path: '/prehled', icon: BrandIconDnesek },
      {
        id: 'pozornost',
        label: 'Pozornost',
        path: '/pozornost',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Bell,
      },
    ],
  },
  {
    id: 'provoz',
    label: 'Provoz',
    items: [
      { id: 'zakazky', label: 'Zakázky', path: '/zakazky', icon: BrandIconZakazky, menuGroup: 'obchod' },
      // Krok AKV1: akvizicni radar (tipy na nabor, uzemi, dopisy, vylouceni).
      // Bez napojeneho radaru je stranka v sedem stavu „Připravujeme" sama
      // (zadna preparing polozka) — menu ji ukazuje vzdy, aby bylo kam se vratit.
      { id: 'akvizice', label: 'Akvizice', path: '/akvizice', icon: Radar, menuGroup: 'obchod' },
      {
        id: 'nemovitosti',
        label: 'Nemovitosti',
        path: '/nemovitosti',
        activePrefixes: ['/nemovitost'],
        icon: Building2,
        menuGroup: 'obchod',
      },
      { id: 'klienti', label: 'Klienti', path: '/klienti', icon: BrandIconKlienti, menuGroup: 'prace' },
      // Krok T3: smlouvy zakazky (vzory REOS + sablony kancelare, editor, verze).
      { id: 'smlouvy', label: 'Smlouvy', path: '/smlouvy', icon: FileSignature, menuGroup: 'prace' },
      // Krok CRM1: pipeline poptavek a zajemcu - uz neni PREPARING, stranka
      // ma sloupce konfigurovatelne kancelari, presun dle stavoveho automatu
      // a import ohlasu z portalu s deduplikaci.
      {
        id: 'poptavky',
        label: 'Poptávky',
        path: '/poptavky',
        icon: Search,
        menuGroup: 'obchod',
      },
      // Krok CRM2: tabule ukolu maklere - uz neni PREPARING, stranka ma
      // pevne sloupce, presun dle zivotniho cyklu ukolu, vazbu na
      // zakazku/klienta/poptavku a automatiku, ktera doplni ukoly z otevrenych
      // bran auditu, terminu a neuhrazenych faktur.
      // Bez `action` — stejne jako `poptavky`: viditelnost v resi server
      // (403), ne menu (viz `nav.spec.ts`, rozhodnuti orchestratora #2).
      //
      // Krok TAB1 bod 1 (R.B. 3. 10.): „Úkoly“ patri do HLAVNIHO menu mezi
      // Zakazky a Klienti, ne do sbalenych „Dalsich nastroju“ — je to
      // kazdodenni obrazovka maklere. Odznak s poctem ukolu PO TERMINU
      // dopisuje `layout.tsx` stejnym zpusobem jako u Smluv a Fakturace;
      // cislo pocita tatáž funkce jako dlazdice „Na tobě“ na Dnesku
      // (`overdueTaskCount`), aby dve mista nerikala dve ruzna cisla.
      { id: 'ukoly', label: 'Úkoly', path: '/ukoly', icon: ClipboardList, menuGroup: 'prace' },
      // Krok CRM4: parovani poptavek s nabidkami a prohlidky pres odkaz - uz
      // nejsou PREPARING (AUD1/UX1 je skryly jako prazdne). Obe do sbalenych
      // „Dalsich nastroju" (DESIGN-UX-V4 pravidlo 6: makler ma v hlavnim menu
      // pet polozek); vstupni bod maji i na Dnesku („Dnešní prohlídky",
      // „Nové shody poptávek") a na karte nemovitosti/poptavky. Bez `action`
      // — stejne jako `poptavky`: viditelnost v resi server (403), ne menu.
      {
        id: 'parovani-poptavek',
        label: 'Párování poptávek',
        path: '/parovani-poptavek',
        icon: GitCompareArrows,
        menuGroup: 'obchod',
      },
      {
        id: 'prohlidky',
        label: 'Prohlídky',
        path: '/prohlidky',
        icon: CalendarClock,
        menuGroup: 'prace',
      },
      {
        id: 'nabidky-rezervace',
        label: 'Nabídky a rezervace',
        path: '/nabidky-rezervace',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Handshake,
      },
      // Krok H1 → krok UXA1: fronta „K doplnění" — otázky, na které server
      // odpověď nenašel. Od UXA1 bez vlastní položky v menu (R.B. 1. 10.:
      // „jako počítadlo v Dnešku a v detailu zakázky, ne jako samostatná
      // položka"); routa i počítadlo v detailu zakázky (case-cockpit.ts)
      // zůstávají, přibylo počítadlo na Dnešku (dnesek.tsx).
      { id: 'k-doplneni', label: 'K doplnění', path: '/k-doplneni', icon: CircleCheck, hiddenFromMenu: true },
      // Krok CRM3: kalendář makléře - už není PREPARING. Stránka má
      // den/týden/měsíc/seznam, zdroje = makléři kanceláře, barvy podle typu
      // události, přetahování i natažení termínu, export iCal a připomínky
      // e-mailem. Bez `action` — stejně jako `poptavky` a `ukoly`:
      // viditelnost v menu neřeší role, to řeší server (viz nav.spec.ts).
      { id: 'kalendar', label: 'Kalendář', path: '/kalendar', icon: BrandIconKalendar, menuGroup: 'prace' },
    ],
  },
  {
    id: 'inzerce',
    label: 'Inzerce',
    items: [
      // Krok UXR3: predloha ma polozku „Portály a inzerce" ve „Nástrojích".
      { id: 'portaly', label: 'Portály a inzerce', path: '/portaly', icon: RefreshCw, menuGroup: 'obchod' },
      {
        id: 'publikace',
        label: 'Publikace',
        path: '/publikace',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Megaphone,
      },
      {
        id: 'naklady-inzerce',
        label: 'Náklady na inzerci',
        path: '/naklady-inzerce',
        icon: Coins,
        menuGroup: 'penize',
      },
      { id: 'aukce', label: 'Aukce', path: '/aukce', preparing: true, pilotVisibility: 'hidden', icon: Gavel },
      // Krok KAMP2 → krok UXA1: e-mailová kampaň realitním kancelářím
      // (trychtýř, STOP, strop). Endpoint hlídá roli owner/admin
      // (admin-campaigns.ts) — menu od UXA1 taky (`ownerAdminOnly`), ať se
      // položka zbytečně nenabízí roli, která na ni stejně dostane 403.
      { id: 'kampane', label: 'Kampaně', path: '/kampane', icon: Megaphone, menuGroup: 'kancelar', ownerAdminOnly: true },
    ],
  },
  {
    id: 'web',
    label: 'Web',
    items: [
      {
        id: 'web-kancelare',
        label: 'Web kanceláře',
        path: '/web-kancelare',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Globe,
      },
      {
        id: 'microsite',
        label: 'Microsite nemovitosti',
        path: '/microsite',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: LayoutTemplate,
      },
      { id: 'weby-projektu', label: 'Weby projektů', path: '/weby-projektu', preparing: true, pilotVisibility: 'hidden', icon: Layers },
    ],
  },
  {
    id: 'komunikace',
    label: 'Komunikace',
    items: [
      // Krok AS3: plocha Asistentky (ne PREPARING) — mezi nastroji, ne
      // v hlavnim menu maklere (UX v4 pravidlo 6: makler ma 5 polozek).
      { id: 'asistentka', label: 'Asistentka', path: '/asistentka', icon: Sparkles, menuGroup: 'kancelar' },
      {
        id: 'schranka',
        label: 'Schránka',
        path: '/schranka',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Mail,
      },
      {
        id: 'mailing',
        label: 'Hromadný mailing',
        path: '/mailing',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Send,
      },
      {
        id: 'sms',
        label: 'SMS',
        path: '/sms',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: MessageSquare,
      },
      {
        id: 'notifikace',
        label: 'Notifikace',
        path: '/notifikace',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: BellRing,
      },
      {
        id: 'emailove-schranky',
        label: 'E-mailové schránky a aliasy',
        path: '/emailove-schranky',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: AtSign,
      },
    ],
  },
  {
    id: 'dokumenty',
    label: 'Dokumenty',
    items: [
      {
        id: 'dokumenty',
        label: 'Dokumenty',
        path: '/dokumenty',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: FileText,
      },
      {
        id: 'smlouvy-sablony',
        label: 'Smlouvy a šablony',
        path: '/smlouvy-sablony',
        icon: FileSignature,
        menuGroup: 'prace',
      },
      // Krok T5 bod 3 → krok UXA1: „Elektronické podpisy" mela vlastni
      // polozku v menu, R.B. 1. 10. ji chce jako filtr „Podepisuje se" v
      // seznamu Smluv (duplicita s menu-polozkou „Smlouvy"), ne dalsi radek
      // menu. Routa zustava funkcni; `badge` uz nikdo nevykresluje (polozka
      // v menu neni), proto layout.tsx `pendingSignatureCount()` nevola —
      // zustava jen jako definice pro pripad, kdy se polozka vrati.
      {
        id: 'elektronicke-podpisy',
        label: 'Elektronické podpisy',
        path: '/elektronicke-podpisy',
        icon: PenLine,
        badge: 'pendingSignatures',
        hiddenFromMenu: true,
      },
      { id: 'reporty', label: 'Reporty', path: '/reporty', icon: BrandIconReport, menuGroup: 'prehledy' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { id: 'fakturace', label: 'Fakturace', path: '/fakturace', icon: ReceiptText, menuGroup: 'penize' },
      {
        id: 'naklady-nemovitosti',
        label: 'Náklady nemovitosti',
        path: '/naklady-nemovitosti',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Wallet,
      },
      // Krok O3 → krok UXA1: inbox faktur od portalu (schvaleni rozpadu
      // nakladu). R.B. 1. 10. ho chce jen pro spravce/owner — bezny maklet
      // se schvalovanim faktur portalu nezabyva.
      {
        id: 'neprirazene-naklady',
        label: 'Nepřiřazené náklady',
        path: '/neprirazene-naklady',
        icon: Inbox,
        menuGroup: 'penize',
        ownerAdminOnly: true,
      },
      { id: 'banka-platby', label: 'Banka a platby', path: '/banka-platby', preparing: true, pilotVisibility: 'hidden', icon: Landmark },
      // Krok AI9 bod 2: manazerský přehled (zakázky bez pohybu) — owner/admin
      // only, stejně jako `neprirazene-naklady`. Krok UXN1 ji vraci do
      // skutecneho menu (skupina Peníze) — zadani UXN1 ji vyjmenovava
      // vyslovne („Ekonomika kanceláře jen pokud ji nevyřadil UXK3b" — nevyřadil).
      {
        id: 'ekonomika-kancelare',
        label: 'Ekonomika kanceláře',
        path: '/ekonomika-kancelare',
        icon: TrendingUp,
        menuGroup: 'penize',
        ownerAdminOnly: true,
      },
    ],
  },
  {
    id: 'analyzy',
    label: 'Analýzy',
    items: [
      { id: 'statistiky', label: 'Statistiky', path: '/statistiky', preparing: true, pilotVisibility: 'hidden', icon: BarChart3 },
      { id: 'zdroje-klientu', label: 'Zdroje klientů', path: '/zdroje-klientu', preparing: true, pilotVisibility: 'hidden', icon: Share2 },
    ],
  },
  {
    id: 'sprava',
    label: 'Správa',
    items: [
      {
        id: 'uzivatele-role',
        label: 'Uživatelé a role',
        path: '/uzivatele-role',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: ShieldCheck,
        action: 'identity.member.invite',
      },
      {
        id: 'pobocky-tymy',
        label: 'Pobočky a týmy',
        path: '/pobocky-tymy',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Network,
      },
      {
        id: 'zdroje',
        label: 'Zdroje dat',
        path: '/zdroje',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Database,
      },
      {
        id: 'import-migrace',
        label: 'Import a migrace',
        path: '/import-migrace',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Upload,
      },
      {
        id: 'gdpr',
        label: 'GDPR',
        path: '/gdpr',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Lock,
      },
      {
        id: 'zalohy-provoz',
        label: 'Zálohy a provoz',
        path: '/zalohy-provoz',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: HardDriveDownload,
      },
      {
        id: 'domeny-hosting',
        label: 'Domény a hosting',
        path: '/domeny-hosting',
        preparing: true,
        pilotVisibility: 'hidden',
        icon: Server,
      },
      // Krok FAK3: predpisy najemneho, mesicni davka, uhrady a vyuctovani sluzeb.
      {
        id: 'sprava-bytu',
        label: 'Správa nájmů',
        path: '/sprava-bytu',
        icon: KeyRound,
        action: 'finance.rent.read',
        menuGroup: 'kancelar',
      },
      // Krok UXN1 (zrusuje UXA1): „Nastavení" se vraci do skutecneho menu
      // (skupina Kancelář) — avatar menu uz nese jen ucet (jmeno, organizace,
      // jazyk, velikost pisma, vzhled, Muj ucet, odhlaseni), zadny pracovni
      // modul. Stranka sama nese link na „Co chystáme".
      {
        id: 'nastaveni',
        label: 'Nastavení',
        path: '/nastaveni',
        icon: Settings,
        menuGroup: 'kancelar',
      },
      // Krok AUD1 → krok UXA1: R.B. 1. 10. chce Audit jen pro spravce/owner —
      // `action` (grant i pro `legal`) zustava pro API, `ownerAdminOnly`
      // zuzuje jen to, co se nabizi v menu beznemu makleti.
      {
        id: 'audit',
        label: 'Audit',
        path: '/audit',
        icon: ScrollText,
        action: 'audit.log.read',
        menuGroup: 'kancelar',
        ownerAdminOnly: true,
      },
      {
        id: 'schvaleni',
        label: 'Schválení',
        path: '/schvaleni',
        icon: ClipboardCheck,
        action: 'workflow.approval.decide',
        menuGroup: 'kancelar',
        ownerAdminOnly: true,
      },
    ],
  },
  // ADM1 bod 7: polozky se stitkem "Obchod brzy" (pilotVisibility 'soon')
  // uz nejsou rozeseté mezi funkcnimi polozkami sve tematicke sekce - jedna
  // spolecna sekce na konci menu, at je jasne oddelene, co jeste nejede.
  {
    id: 'pripravujeme',
    label: 'Připravujeme',
    items: [
      {
        id: 'statistiky-inzerce',
        label: 'Statistiky inzerce',
        path: '/statistiky-inzerce',
        preparing: true, pilotVisibility: 'soon',
        icon: Activity,
      },
      {
        id: 'ohlasy-portalu',
        label: 'Ohlasy z portálů',
        path: '/ohlasy-portalu',
        preparing: true, pilotVisibility: 'soon',
        icon: MessageSquareReply,
      },
      { id: 'vizitka-maklere', label: 'Vizitka makléře', path: '/vizitka-maklere', preparing: true, pilotVisibility: 'soon', icon: IdCard },
      {
        id: 'klientsky-portal',
        label: 'Klientský portál',
        path: '/klientsky-portal',
        preparing: true, pilotVisibility: 'soon',
        icon: UserRoundCheck,
      },
      {
        id: 'provize',
        label: 'Provize',
        path: '/provize',
        preparing: true, pilotVisibility: 'soon',
        icon: Percent,
        action: 'finance.commission.read',
      },
      { id: 'trh-srovnani', label: 'Trh a srovnání', path: '/trh-srovnani', preparing: true, pilotVisibility: 'soon', icon: LineChart },
      { id: 'ocenovani', label: 'Oceňování', path: '/ocenovani', preparing: true, pilotVisibility: 'soon', icon: Scale },
    ],
  },
];

/** Id vsech polozek — pouziva se pro test uplnosti a pro command-palettu. */
export const STATIC_NAV_IDS: ReadonlySet<string> = new Set(
  NAV_SECTIONS.flatMap((section) => section.items.map((item) => item.id)),
);

/**
 * Krok UXN1 — poradi a nadpisy skutecne vykreslenych skupin leveho menu
 * (sidebar, mobilni „Více", Ctrl+K). Vsech pet skupin ma nadpis — „Dnešek"
 * a „Nová zakázka" stoji NAD nimi bez nadpisu (`layout.tsx` je sklada
 * primo, nejsou v `MenuGroup`).
 */
export const MENU_GROUP_ORDER: readonly MenuGroup[] = ['obchod', 'prace', 'penize', 'prehledy', 'kancelar'];

/** Nadpis skupiny (predloha UXN1: krátký, tlumený titulek nad polozkami). */
export const MENU_GROUP_LABELS: Readonly<Record<MenuGroup, string>> = {
  obchod: 'Obchod',
  prace: 'Práce',
  penize: 'Peníze',
  prehledy: 'Přehledy',
  kancelar: 'Kancelář',
};

/**
 * Krok UXN1 — poradi polozek UVNITR skupiny. Zadani jmenuje jadro kazde
 * skupiny vyslovne; poradi v `NAV_SECTIONS` je dane devitti tematickymi
 * sekcemi (Reporty ziji v sekci Dokumenty, Fakturace az ve Financich) —
 * proste proiterovat sekce by dalo jine poradi, nez ma makler videt. Proto
 * je tady poradi vypsane, ne odvozene. Test `nav.spec.ts` ho porovnava
 * `toEqual` bez `sort()`, takze pripadny rozchod se zadanim spadne.
 */
const MENU_ITEM_ORDER: Readonly<Record<MenuGroup, readonly string[]>> = {
  obchod: ['zakazky', 'nemovitosti', 'poptavky', 'parovani-poptavek', 'akvizice', 'portaly'],
  prace: ['ukoly', 'kalendar', 'klienti', 'smlouvy', 'smlouvy-sablony', 'prohlidky'],
  penize: ['fakturace', 'naklady-inzerce', 'neprirazene-naklady', 'ekonomika-kancelare'],
  prehledy: ['reporty'],
  // ROZHODNUTI UXN1-2: Audit (admin diagnostika provozu) stoji u ostatnich
  // „provoznich" polozek kancelare, zadani ho vyslovne nejmenuje.
  kancelar: ['kampane', 'asistentka', 'schvaleni', 'sprava-bytu', 'nastaveni', 'audit'],
};

/**
 * Vsechny polozky, ktere se smi ukazat ve skutecnem menu (pred filtrem role),
 * ve skupinach a poradi podle zadani. Polozka s `menuGroup`, ktera v
 * `MENU_ITEM_ORDER` chybi, by v menu tise zmizela — proto to hlida test
 * „kazda polozka s menuGroup je v MENU_ITEMS" v `nav.spec.ts`.
 */
export const MENU_ITEMS: readonly Blok1NavItem[] = MENU_GROUP_ORDER.flatMap((group) => {
  const inGroup = NAV_SECTIONS.flatMap((section) => section.items).filter((item) => item.menuGroup === group);
  return MENU_ITEM_ORDER[group].flatMap((id) => inGroup.filter((item) => item.id === id));
});

/**
 * Krok QAFIX3 (nalez N06 QA 20. 10. 2026) — „Objednávky reportů"
 * (`/objednavky-fronta`) NEMELA V MENU ZADNOU POLOZKU. Obrazovka existuje od
 * kroku C3 (fronta provozovatele) a od NAP11 na ni stoji i karta „Nová
 * objednávka reportu", takze jediny zpusob, jak se na ni kancelar dostala,
 * bylo napsat cestu do adresniho radku.
 *
 * Polozka stoji MIMO `NAV_SECTIONS` (a tim i mimo `MENU_ITEMS`) ze stejneho
 * duvodu jako `NEW_CASE_ITEM`: `NAV_SECTIONS` je 57 polozek cilove struktury
 * BLOKu 2.5 a skupiny UXN1 jsou vyjmenovane kontraktem v `nav.spec.ts`;
 * tohle je vstup PODLE OPRAVNENI (`commerce.order.create` — tyz grant, ktery
 * autorizuje `POST /v1/orders`), ne dalsi modul cilove struktury.
 * Definice je JEDNA a cte ji panel (`layout.tsx`) i Ctrl+K
 * (`command-palette.tsx`) pres `SHELL_MENU_ITEMS`.
 */
export const ORDER_QUEUE_ITEM: Blok1NavItem = {
  id: 'objednavky-fronta',
  label: 'Objednávky reportů',
  path: '/objednavky-fronta',
  icon: ShoppingCart,
  action: 'commerce.order.create',
  menuGroup: 'prehledy',
};

/**
 * Krok DEV1 — „Developerské projekty" (`/projekty`): projekt, jeho jednotky,
 * cenik a export na portaly.
 *
 * Polozka stoji MIMO `NAV_SECTIONS` (a tim i mimo `MENU_ITEMS`) ze stejneho
 * duvodu jako `ORDER_QUEUE_ITEM`: `NAV_SECTIONS` je vyjmenovana cilova
 * struktura BLOKu 2.5, jejiz skupiny a pocty hlida kontrakt v `nav.spec.ts`.
 * Tohle je vstup PODLE OPRAVNENI (`property.property.create` — tyz grant,
 * ktery autorizuje zalozeni projektu), ne dalsi modul cilove struktury.
 * Definice je JEDNA a cte ji panel (`layout.tsx`) i Ctrl+K
 * (`command-palette.tsx`) pres `SHELL_MENU_ITEMS`.
 */
export const DEVELOPER_PROJECTS_ITEM: Blok1NavItem = {
  id: 'projekty',
  label: 'Developerské projekty',
  path: '/projekty',
  icon: Layers,
  action: 'property.property.create',
  menuGroup: 'obchod',
};

/**
 * Krok APP2 bod 3 (bod 21 zadani) — „Nápověda" (`/napoveda`).
 *
 * Cesta do APP2 neexistovala, takze makler, ktery ji napsal, skoncil na 404
 * (ceska 404 uz je nasazena, krok polish8 — ale napoveda je to, co hledal).
 * Polozka stoji MIMO `NAV_SECTIONS` (a tim i mimo `MENU_ITEMS`) ze stejneho
 * duvodu jako `ORDER_QUEUE_ITEM` a `DEVELOPER_PROJECTS_ITEM`: `NAV_SECTIONS`
 * je vyjmenovana cilova struktura BLOKu 2.5 a skupiny UXN1 jsou kontrakt
 * v `nav.spec.ts`. Tohle neni dalsi modul produktu, je to pomoc s nim —
 * a vidi ji KAZDA role (zadne `action`). Definice je JEDNA a cte ji panel
 * (`layout.tsx`) i Ctrl+K (`command-palette.tsx`) pres `SHELL_MENU_ITEMS`.
 */
export const HELP_ITEM: Blok1NavItem = {
  id: 'napoveda',
  label: 'Nápověda',
  path: '/napoveda',
  icon: CircleQuestionMark,
  menuGroup: 'kancelar',
};

/**
 * Polozky skutecneho menu: skupiny UXN1 (`MENU_ITEMS`) a vstupy podle
 * opravneni. Panel i Ctrl+K ctou TENTYZ seznam — dve mista by se rozesla
 * (na to dojezdil krok UXA1). Role-gating dal resi `isNavItemVisible`
 * (`capabilities.ts`), poradi ve skupine zustava poradi tohoto seznamu.
 */
export const SHELL_MENU_ITEMS: readonly Blok1NavItem[] = [
  ...MENU_ITEMS,
  ORDER_QUEUE_ITEM,
  DEVELOPER_PROJECTS_ITEM,
  HELP_ITEM,
];

/** Vyhleda polozku podle id v `NAV_SECTIONS` — pro pinned polozky nad skupinami. */
export function navItemById(id: string): Blok1NavItem {
  const item = NAV_SECTIONS.flatMap((section) => section.items).find((entry) => entry.id === id);
  if (item === undefined) throw new Error(`nav item ${id} missing`);
  return item;
}

/**
 * Spodni lista mobilu — TRI polozky z menu, mezi ne „Nová" (`NEW_CASE_ITEM`,
 * vklada `layout.tsx`) a jako PATOU dlazdici „Více" (vykresluje `AdminShell`,
 * protoze otevira jeho panel).
 *
 * Krok UXF4 bod 1 (nalez N1 reauditu GPT 10. 10. 2026) — do UXF4 byla patou
 * dlazdici „Klienti" a „Více" stalo nahore jen jako ikona `…`. Zadani UXN1
 * pritom pate misto dava „Více" s popiskem: bez nej nevede z telefonu
 * oznacena cesta k Fakturaci, Smlouvam, Portalum ani Reportum. „Klienti"
 * nezmizeli — jsou ve skupine Práce v „Více" (stejny SSOT jako leve menu).
 */
export const MOBILE_BAR_IDS: readonly string[] = ['dnesek', 'zakazky', 'kalendar'];

/**
 * Akce zalozeni zakazky — tretí dlazdice spodni listy i horni polozka nad
 * skupinami leveho menu.
 *
 * Krok UXF4 bod 4 (nalez #2 auditu 9. 10.): v menu stoji CELY nazev cile
 * („Nová zakázka"), protoze samotne „Nová" neprozradi, co se zaklada. Na
 * spodni liste mobilu se slovo do dlazdice nevejde, takze tam zustava
 * `shortLabel` — a pristupny nazev je i tak cely (`aria-label` v `layout.tsx`).
 */
export const NEW_CASE_ITEM: Pick<Blok1NavItem, 'id' | 'label' | 'path' | 'icon'> & {
  readonly shortLabel: string;
} = {
  id: 'nova-zakazka',
  label: 'Nová zakázka',
  shortLabel: 'Nová',
  path: '/zakazky/novy',
  icon: BrandIconNova,
};

/**
 * Krok UXP4c (P3, nalez „na Nové zakázce je zvýrazněné Zakázky místo Nová"):
 * `NEW_CASE_ITEM.path` je podcesta `/zakazky`, takze by `isNavItemActive`
 * oznacilo OBE polozky — menu i spodni lista musi na teto strance „Zakázky"
 * VYPNOUT a „Nová" zapnout, proto je shoda tady, na jednom miste s `NEW_CASE_ITEM`.
 */
export function isNewCaseActivePath(pathname: string): boolean {
  return pathname === NEW_CASE_ITEM.path || pathname.startsWith(`${NEW_CASE_ITEM.path}/`);
}

/** Vstup pro filtrovani menu — oba flagy jsou per tenant (`@reos/flags`). */
export interface NavFlags {
  /** `pilot.menu` (krok O1): pilotni režim = v menu jen to, co fakt jede. */
  readonly pilotMenu: boolean;
  /** `pilot.showHiddenSections` (krok UX1): odemyká skupinu „skrýt do napojení“. */
  readonly showHiddenSections?: boolean;
}

/** Je tato položka v daném stavu vidět v menu? Jedno místo — menu i router. */
export function isNavItemVisible(item: Blok1NavItem, flags: NavFlags): boolean {
  if (item.preparing !== true) return true;
  // Pilotní režim je přísnější než přepínač skupin: schová všechny preparing.
  if (flags.pilotMenu) return false;
  return item.pilotVisibility !== 'hidden' || flags.showHiddenSections === true;
}

/**
 * Krok O1 (`pilot.menu`) + krok UX1 (`pilot.showHiddenSections`): filtr nad
 * JEDNÍM zdrojem pravdy (`NAV_SECTIONS`). Výchozí stav je výchozí stav
 * pilotu — skupina „skrýt do napojení“ (AUD1 §3.3) je skrytá, skupina
 * „brzy“ (AUD1 §3.2) viditelná s jednotným štítkem.
 */
export function buildNav({
  pilotMenu,
  showHiddenSections = false,
}: NavFlags): readonly Blok1NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      isNavItemVisible(item, { pilotMenu, showHiddenSections }),
    ),
  })).filter((section) => section.items.length > 0);
}

/**
 * Skupina, do které cesta patří (krok UX1). Stránka PŘIPRAVUJE SE z ní
 * bere jednotný štítek: „brzy“ sekce nese „Obchod brzy“ (AUD1 §3.2),
 * zbytek „PŘIPRAVUJE SE“.
 */
export function pilotVisibilityForPath(path: string): PilotVisibility | undefined {
  return NAV_SECTIONS.flatMap((section) => section.items).find((entry) => entry.path === path)
    ?.pilotVisibility;
}

/** Cesta, kterou v tomto stavu nevidíme → stranka ji přesměruje na Dnesek. */
export function isPreparingPathHidden(path: string, flags: NavFlags): boolean {
  const item = NAV_SECTIONS.flatMap((section) => section.items).find(
    (entry) => entry.path === path,
  );
  return item !== undefined && !isNavItemVisible(item, flags);
}

/**
 * Aktivni polozka: presna shoda cesty, jeji pod-cesty (/zakazky/:id)
 * a deklarovane prefixy.
 */
export function isNavItemActive(pathname: string, item: Blok1NavItem): boolean {
  const prefixes = [item.path, ...(item.activePrefixes ?? [])];
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * REZIM SOUSTREDENI (krok SML2 bod 1): obrazovky, na kterych se hlavni menu
 * aplikace NEVYKRESLUJE.
 *
 * Duvod je predloha `docs/design/ux-v3/Smlouva.dc.html`: editor smlouvy je
 * jediny soubor UX v3, ktery tmavy panel menu NEMA — clovek vyplnuje jeden
 * dokument a nema z nej odbihat. Cesta zpet nezmizela: vede jí „‹" v hlavicce
 * editoru a drobecky (Zakázky › zakázka › Smlouvy).
 *
 * Jedna funkce pro cely admin (shell ji cte, test ji overuje) — dva seznamy
 * „kde je menu schovane" by se rozesly.
 */
export function isFocusModeRoute(pathname: string): boolean {
  // Krok UXV4b: Nový inzerát (predloha `Inzerat.dc.html` menu nema — dva
  // kroky s jednim tlacitkem; cesta zpet je „Zrušit" / „‹ Údaje").
  if (/^\/zakazky\/[^/]+\/inzerat$/.test(pathname) || pathname === '/inzerat') return true;
  // `/smlouvy/nova` je VYBER vzoru, ne editor — menu tam zustava.
  // Krok UXV4c: nabor na miste je take rezim soustredeni (predloha `Nabor.png` menu nema).
  if (pathname === '/nabor') return true;
  return /^\/smlouvy\/[^/]+$/.test(pathname) && pathname !== '/smlouvy/nova';
}
